import * as XLSX from 'xlsx';

import { formatarMesLongo, totaisRegime } from './calculos';
import type { DadosIndicadoresMensais } from './tipos';

/**
 * Exporta os indicadores mensais para XLSX no mesmo layout da planilha "KPIs
 * DTP" entregue pelo cliente — 4 abas: Pagamentos PF×PJ, Consolidado + Frota,
 * Médias e razões, e Legenda. Replica `lib/indicadoresMensaisExport.ts` do
 * protótipo, com os nomes de campo em camelCase deste repositório.
 *
 * Geração inteira no navegador (biblioteca `xlsx`, já dependência do
 * projeto) — não depende da API do módulo financeiro, que ainda não existe.
 */
export function exportarIndicadoresMensaisParaXlsx(
  dados: DadosIndicadoresMensais,
  mesRotulo: string,
): void {
  const livro = XLSX.utils.book_new();
  const rotuloMes = formatarMesLongo(mesRotulo).toUpperCase();

  // ===== Aba 1 — Pagamentos PF × PJ =====
  const bloco1: (string | number)[][] = [
    ['RESUMO DOS INDICADORES OPERACIONAIS E DADOS FINANCEIROS - TEG'],
    [`MÊS BASE: ${rotuloMes}`],
    [],
    ['', 'PESSOA FÍSICA', '', '', '', '', '', '', '', 'PESSOA JURÍDICA', '', '', '', ''],
    [
      'DRE',
      'BRUTO (R$)',
      'CRIANÇAS',
      '',
      '',
      'TOTAL KM ADIC',
      'INSS',
      'VL.BR.C/INSS',
      '',
      'BRUTO (R$)',
      'CRIANÇAS',
      '',
      '',
      'TOTAL KM ADIC',
    ],
    ['', '', 'NC', 'CAD', 'TOTAL', '', '', '', '', '', 'NC', 'CAD', 'TOTAL', ''],
  ];
  let totalBrutoPf = 0;
  let totalNcPf = 0;
  let totalCadPf = 0;
  let totalKmPf = 0;
  let totalInss = 0;
  let totalBcInssPf = 0;
  let totalBrutoPj = 0;
  let totalNcPj = 0;
  let totalCadPj = 0;
  let totalKmPj = 0;
  for (const r of dados.porDre) {
    const pf = totaisRegime(r.pf, true);
    const pj = totaisRegime(r.pj, false);
    bloco1.push([
      r.dreCodigo,
      pf.bruto,
      pf.criancasNc,
      pf.criancasCad,
      pf.criancasTotal,
      pf.kmAdic,
      pf.inss,
      pf.brutoComInss,
      '',
      pj.bruto,
      pj.criancasNc,
      pj.criancasCad,
      pj.criancasTotal,
      pj.kmAdic,
    ]);
    totalBrutoPf += pf.bruto;
    totalNcPf += pf.criancasNc;
    totalCadPf += pf.criancasCad;
    totalKmPf += pf.kmAdic;
    totalInss += pf.inss;
    totalBcInssPf += pf.brutoComInss;
    totalBrutoPj += pj.bruto;
    totalNcPj += pj.criancasNc;
    totalCadPj += pj.criancasCad;
    totalKmPj += pj.kmAdic;
  }
  bloco1.push([
    'TOTAL',
    totalBrutoPf,
    totalNcPf,
    totalCadPf,
    totalNcPf + totalCadPf,
    totalKmPf,
    totalInss,
    totalBcInssPf,
    '',
    totalBrutoPj,
    totalNcPj,
    totalCadPj,
    totalNcPj + totalCadPj,
    totalKmPj,
  ]);
  XLSX.utils.book_append_sheet(livro, XLSX.utils.aoa_to_sheet(bloco1), 'Pagamentos PF x PJ');

  // ===== Aba 2 — Consolidado + Frota =====
  const bloco2: (string | number)[][] = [
    [`CONSOLIDADO GERAL + FROTA — ${rotuloMes}`],
    [],
    [
      'GERAL',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      'VEÍCULOS CONVENCIONAIS',
      '',
      'ACESSÍVEIS',
      '',
      'VEÍCULOS CRECHE',
      '',
    ],
    [
      'DRE',
      'BRUTO',
      'CRIANÇAS',
      '',
      'CRIANÇA TRANSP.',
      'TOTAL KM',
      'INSS',
      'VL.BR.C/INSS',
      'PF',
      'PJ',
      'PF',
      'PJ',
      'PF',
      'PJ',
    ],
    ['', '', 'NC', 'CAD', '', '', '', '', '', '', '', '', '', ''],
  ];
  let totalBruto = 0;
  let totalNc = 0;
  let totalCad = 0;
  let totalKm = 0;
  let totalInssGeral = 0;
  let totalBcInss = 0;
  let totalConvPf = 0;
  let totalConvPj = 0;
  let totalAcessPf = 0;
  let totalAcessPj = 0;
  let totalCrechePf = 0;
  let totalCrechePj = 0;
  for (const r of dados.porDre) {
    const pf = totaisRegime(r.pf, true);
    const pj = totaisRegime(r.pj, false);
    const bruto = pf.bruto + pj.bruto;
    const nc = pf.criancasNc + pj.criancasNc;
    const cad = pf.criancasCad + pj.criancasCad;
    const total = nc + cad;
    const km = pf.kmTotal + pj.kmTotal;
    bloco2.push([
      r.dreCodigo,
      bruto,
      nc,
      cad,
      total,
      km,
      pf.inss,
      bruto + pf.inss,
      pf.veiculosConv,
      pj.veiculosConv,
      pf.veiculosAcess,
      pj.veiculosAcess,
      pf.veiculosCreche,
      pj.veiculosCreche,
    ]);
    totalBruto += bruto;
    totalNc += nc;
    totalCad += cad;
    totalKm += km;
    totalInssGeral += pf.inss;
    totalBcInss += bruto + pf.inss;
    totalConvPf += pf.veiculosConv;
    totalConvPj += pj.veiculosConv;
    totalAcessPf += pf.veiculosAcess;
    totalAcessPj += pj.veiculosAcess;
    totalCrechePf += pf.veiculosCreche;
    totalCrechePj += pj.veiculosCreche;
  }
  bloco2.push([
    'TOTAL',
    totalBruto,
    totalNc,
    totalCad,
    totalNc + totalCad,
    totalKm,
    totalInssGeral,
    totalBcInss,
    totalConvPf,
    totalConvPj,
    totalAcessPf,
    totalAcessPj,
    totalCrechePf,
    totalCrechePj,
  ]);
  XLSX.utils.book_append_sheet(livro, XLSX.utils.aoa_to_sheet(bloco2), 'Consolidado + Frota');

  // ===== Aba 3 — Médias e razões =====
  const bloco3: (string | number)[][] = [
    [`MÉDIAS E RAZÕES — ${rotuloMes}`],
    [],
    [
      'DRE',
      'VALOR CRIANÇA/DIA (R$)',
      'CONDUTORES REMUNERADOS',
      '',
      '',
      'REMUNERAÇÃO MÉDIA/CONDUTOR',
      '',
      '',
      'CRIANÇA/VEÍCULO',
      '',
      '',
      'KM MENSAL/CONDUTOR',
      '',
      '',
    ],
    ['', '', 'PF', 'PJ', 'GERAL', 'PF', 'PJ', 'MÉDIA', 'PF', 'PJ', 'MÉDIA', 'PF', 'PJ', 'MÉDIA'],
  ];
  for (const r of dados.porDre) {
    const pf = totaisRegime(r.pf, true);
    const pj = totaisRegime(r.pj, false);
    const brutoTotal = pf.bruto + pj.bruto;
    const criancasTotal = pf.criancasTotal + pj.criancasTotal;
    const vPf = pf.veiculosTotal;
    const vPj = pj.veiculosTotal;
    const cPf = pf.condutores;
    const cPj = pj.condutores;
    const cGeral = cPf + cPj;
    const dias = r.diasLetivos || 1;
    const valorCriancaDia = criancasTotal > 0 ? brutoTotal / (criancasTotal * dias) : 0;
    const remPf = cPf > 0 ? pf.bruto / cPf : 0;
    const remPj = cPj > 0 ? pj.bruto / cPj : 0;
    const remMed = cGeral > 0 ? brutoTotal / cGeral : 0;
    const criancaVeicPf = vPf > 0 ? pf.criancasTotal / vPf : 0;
    const criancaVeicPj = vPj > 0 ? pj.criancasTotal / vPj : 0;
    const criancaVeicMed = vPf + vPj > 0 ? criancasTotal / (vPf + vPj) : 0;
    const kmCondutorPf = cPf > 0 ? pf.kmTotal / cPf : 0;
    const kmCondutorPj = cPj > 0 ? pj.kmTotal / cPj : 0;
    const kmCondutorMed = cGeral > 0 ? (pf.kmTotal + pj.kmTotal) / cGeral : 0;
    bloco3.push([
      r.dreCodigo,
      valorCriancaDia,
      cPf,
      cPj,
      cGeral,
      remPf,
      remPj,
      remMed,
      criancaVeicPf,
      criancaVeicPj,
      criancaVeicMed,
      kmCondutorPf,
      kmCondutorPj,
      kmCondutorMed,
    ]);
  }
  XLSX.utils.book_append_sheet(livro, XLSX.utils.aoa_to_sheet(bloco3), 'Medias e razoes');

  // ===== Aba 4 — Legenda =====
  const legenda: (string | number)[][] = [
    ['LEGENDA'],
    [],
    ['PF', 'Pessoa Física'],
    ['PJ', 'Pessoa Jurídica'],
    [],
    ['DRE', 'Diretoria Regional de Educação'],
  ];
  for (const r of dados.porDre) legenda.push([r.dreCodigo, r.dreNome]);
  XLSX.utils.book_append_sheet(livro, XLSX.utils.aoa_to_sheet(legenda), 'Legenda');

  XLSX.writeFile(livro, `Indicadores_TEG_${mesRotulo}.xlsx`);
}
